"use client"
import { useState } from "react"
import { Input } from "@/components/Primitives/Input"
import { Label } from "@/components/Primitives/Label"
import { Button } from "@/components/Primitives/Button"
import { Text } from "@/components/Primitives/Text"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalClose,
} from "@/components/Primitives/Modal/modal"
import { EyeClosed, Eye } from "lucide-react"
import { getPasswordSchema, getPasswordMessages } from "@/lib/schemas/auth"
import { getPasswordValidation } from "@/lib/utils/utils"
import { Spinner } from "@/components/Primitives/Spinner"
import { useConfig } from "@/hooks/useConfig"
import { PasswordRequirements } from "@/components/Primitives/PasswordRequirements/passwordRequirements"
import { Icon } from "@/components/Primitives"

interface PasswordModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (currentPassword: string, newPassword: string) => Promise<void>
  isUpdating: boolean
  error?: string
}

export function PasswordModal({
  isOpen,
  onClose,
  onSubmit,
  isUpdating,
  error,
}: PasswordModalProps) {
  const { t } = useConfig()
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)

  const passwordValidation = getPasswordValidation(newPassword, t)
  const isPasswordValid = getPasswordSchema(t).safeParse(newPassword).success
  const passwordRequirements = getPasswordMessages(t).map((label) => ({
    label,
  }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await onSubmit(currentPassword, newPassword)
    setCurrentPassword("")
    setNewPassword("")
  }

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent>
        <ModalHeader className="">
          <div className="flex justify-end items-center">
            <ModalClose className="right-0 top-0" />
          </div>
          <Text variant="h4" bold className="text-left">
            {t("config_profile_password_title")}
          </Text>
        </ModalHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 mt-4">
          <div>
            <Label htmlFor="currentPassword">
              {t("config_profile_password_label")}
            </Label>
            <div className="relative">
              <Input
                id="currentPassword"
                type={showCurrentPassword ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                disabled={isUpdating}
                placeholder={t("config_profile_password_placeholder")}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 px-2"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
              >
                {showCurrentPassword ? (
                  <Icon icon={EyeClosed} className="w-4 h-4" />
                ) : (
                  <Icon icon={Eye} className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>
          <div>
            <Label htmlFor="newPassword">
              {t("config_profile_new_password_label")}
            </Label>
            <div className="relative mb-2">
              <Input
                id="newPassword"
                type={showNewPassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isUpdating}
                placeholder={t("config_profile_new_password_placeholder")}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 px-2"
                onClick={() => setShowNewPassword(!showNewPassword)}
              >
                {showNewPassword ? (
                  <Icon icon={EyeClosed} className="w-4 h-4" />
                ) : (
                  <Icon icon={Eye} className="w-4 h-4" />
                )}
              </Button>
            </div>
            <div className="mt-4">
              <PasswordRequirements
                password={newPassword}
                validation={passwordValidation}
                requirements={passwordRequirements}
              />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="submit" disabled={isUpdating || !isPasswordValid}>
              {isUpdating ? (
                <Spinner className="w-4 h-4 animate-spin" />
              ) : (
                t("config_profile_button_update")
              )}
            </Button>
          </div>
          {error && (
            <Text variant="small" className="text-red-500 text-center">
              {error}
            </Text>
          )}
        </form>
      </ModalContent>
    </Modal>
  )
}
